import React, { useEffect } from 'react';
import SEO from '../components/SEO';
import './PrivacyPolicy.css';

const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="privacy-container">
      <SEO 
        title="Privacy Policy - BlackKiteAI"
        description="BlackKiteAI privacy policy. Learn how we protect your data and ensure privacy during AI-powered interview assistance."
        canonical="https://www.blackkiteai.com/privacy-policy"
      />
      <div className="privacy-content">
        <h1>Privacy Policy</h1>
        <p className="last-updated">Last Updated: January 5, 2026</p>

        <section>
          <p>
            BlackKiteAI ("we," "us," or "our") is committed to protecting your privacy and ensuring 
            the security of your personal information. This Privacy Policy explains how we collect, 
            use, disclose, and safeguard your information when you use our AI-powered risk scoring, 
            threat intelligence, and security analytics platform ("Service").
          </p>
          <p>
            By using our Service, you consent to the data practices described in this Privacy Policy. 
            If you do not agree with our policies and practices, please do not use our Service.
          </p>
        </section>

        <section>
          <h2>1. Information We Collect</h2>
          
          <h3>1.1 Personal Information You Provide</h3>
          <p>We collect information that you voluntarily provide to us when you:</p>
          <ul>
            <li><strong>Create an Account:</strong> Name, email address, password, company name, job title</li>
            <li><strong>Make Purchases:</strong> Billing information, payment details (processed securely through third-party payment processors)</li>
            <li><strong>Contact Support:</strong> Email address, name, message content, and any information you choose to provide</li>
            <li><strong>Use the Service:</strong> Resumes, documents, interview transcripts, queries, and other content you upload or generate</li>
            <li><strong>Complete Surveys or Forms:</strong> Feedback, preferences, and responses to questionnaires</li>
          </ul>

          <h3>1.2 Automatically Collected Information</h3>
          <p>When you access or use our Service, we automatically collect:</p>
          <ul>
            <li><strong>Device Information:</strong> IP address, browser type and version, operating system, device identifiers</li>
            <li><strong>Usage Data:</strong> Pages visited, features used, time spent on pages, click data, session duration</li>
            <li><strong>Log Data:</strong> Access times, error logs, API calls, system activity</li>
            <li><strong>Cookies and Similar Technologies:</strong> We use cookies, web beacons, and similar tracking technologies (see Section 5)</li>
            <li><strong>Location Data:</strong> General geographic location based on IP address</li>
          </ul>

          <h3>1.3 Information from Third Parties</h3>
          <p>We may receive information from:</p>
          <ul>
            <li><strong>Authentication Services:</strong> If you sign in using third-party services (e.g., Google, Microsoft)</li>
            <li><strong>Payment Processors:</strong> Transaction confirmation and payment status</li>
            <li><strong>Analytics Providers:</strong> Aggregated usage statistics and performance metrics</li>
            <li><strong>Public Sources:</strong> Publicly available cybersecurity threat data and risk intelligence</li>
          </ul>

          <h3>1.4 Sensitive Information</h3>
          <p>
            We do not intentionally collect sensitive personal information such as social security 
            numbers, health information, or financial account details (other than payment information 
            processed by our payment processors). If you upload documents containing sensitive 
            information, you do so at your own discretion and risk.
          </p>
        </section>

        <section>
          <h2>2. How We Use Your Information</h2>
          <p>We use the information we collect for the following purposes:</p>
          
          <h3>2.1 Service Provision and Improvement</h3>
          <ul>
            <li>Provide, operate, and maintain the Service</li>
            <li>Process your transactions and manage your account</li>
            <li>Generate AI-powered risk assessments, threat intelligence, and security analytics</li>
            <li>Train and improve our machine learning models and algorithms</li>
            <li>Develop new features and enhance existing functionality</li>
            <li>Monitor and analyze usage patterns and trends</li>
            <li>Conduct research and development</li>
          </ul>

          <h3>2.2 Communication</h3>
          <ul>
            <li>Send you service-related communications (account verification, updates, security alerts)</li>
            <li>Respond to your inquiries, requests, and support tickets</li>
            <li>Send promotional communications, newsletters, and marketing materials (with your consent)</li>
            <li>Request feedback and conduct surveys</li>
          </ul>

          <h3>2.3 Security and Compliance</h3>
          <ul>
            <li>Detect, prevent, and address fraud, abuse, and security incidents</li>
            <li>Enforce our Terms and Conditions and other policies</li>
            <li>Comply with legal obligations and regulatory requirements</li>
            <li>Protect our rights, property, and safety, and those of our users</li>
            <li>Conduct security audits and vulnerability assessments</li>
          </ul>

          <h3>2.4 Analytics and Personalization</h3>
          <ul>
            <li>Analyze usage patterns to improve user experience</li>
            <li>Personalize content and recommendations</li>
            <li>Measure the effectiveness of our marketing campaigns</li>
            <li>Create aggregated, anonymized statistics for business purposes</li>
          </ul>
        </section>

        <section>
          <h2>3. Legal Basis for Processing (GDPR)</h2>
          <p>
            If you are located in the European Economic Area (EEA), United Kingdom, or Switzerland, 
            we process your personal data based on the following legal grounds:
          </p>
          <ul>
            <li><strong>Contractual Necessity:</strong> Processing necessary to fulfill our contract with you (providing the Service)</li>
            <li><strong>Legitimate Interests:</strong> Processing necessary for our legitimate business interests (improving the Service, security, fraud prevention)</li>
            <li><strong>Consent:</strong> Where you have given explicit consent (marketing communications, optional features)</li>
            <li><strong>Legal Obligation:</strong> Processing required to comply with legal obligations</li>
          </ul>
        </section>

        <section>
          <h2>4. How We Share Your Information</h2>
          <p>We do not sell your personal information. We may share your information in the following circumstances:</p>

          <h3>4.1 Service Providers</h3>
          <p>
            We engage third-party companies to perform services on our behalf, including:
          </p>
          <ul>
            <li>Cloud hosting and infrastructure providers (e.g., AWS, Azure, Google Cloud)</li>
            <li>Payment processing services (e.g., Stripe, PayPal)</li>
            <li>Email delivery and communication platforms</li>
            <li>Analytics and monitoring tools</li>
            <li>Customer support platforms</li>
            <li>AI and machine learning service providers</li>
          </ul>
          <p>
            These service providers are contractually obligated to protect your information and use 
            it only for the purposes for which it is disclosed.
          </p>

          <h3>4.2 Business Transfers</h3>
          <p>
            If we are involved in a merger, acquisition, asset sale, bankruptcy, or other business 
            transaction, your information may be transferred as part of that transaction. We will 
            notify you of any such change in ownership or control of your personal information.
          </p>

          <h3>4.3 Legal Requirements</h3>
          <p>We may disclose your information when required to:</p>
          <ul>
            <li>Comply with legal obligations, court orders, or government requests</li>
            <li>Enforce our Terms and Conditions or other agreements</li>
            <li>Protect the rights, property, or safety of BlackKiteAI, our users, or the public</li>
            <li>Detect, prevent, or address fraud, security, or technical issues</li>
          </ul>

          <h3>4.4 With Your Consent</h3>
          <p>
            We may share your information for any other purpose with your explicit consent.
          </p>

          <h3>4.5 Aggregated and Anonymized Data</h3>
          <p>
            We may share aggregated, anonymized, or de-identified data that cannot reasonably be 
            used to identify you for research, analytics, marketing, or other business purposes.
          </p>
        </section>

        <section>
          <h2>5. Cookies and Tracking Technologies</h2>
          
          <h3>5.1 What Are Cookies?</h3>
          <p>
            Cookies are small text files stored on your device that help us recognize you and 
            remember your preferences. We use cookies and similar technologies (web beacons, pixels, 
            local storage) to enhance your experience.
          </p>

          <h3>5.2 Types of Cookies We Use</h3>
          <ul>
            <li><strong>Essential Cookies:</strong> Required for the Service to function (authentication, security, session management)</li>
            <li><strong>Performance Cookies:</strong> Collect information about how you use the Service (analytics, error tracking)</li>
            <li><strong>Functional Cookies:</strong> Remember your preferences and settings</li>
            <li><strong>Marketing Cookies:</strong> Track your activity to deliver personalized ads and measure campaign effectiveness</li>
          </ul>

          <h3>5.3 Managing Cookies</h3>
          <p>
            You can control cookies through your browser settings. However, disabling cookies may 
            affect the functionality of the Service. You can also opt out of interest-based advertising 
            through industry opt-out tools.
          </p>
        </section>

        <section>
          <h2>6. Data Retention</h2>
          <p>
            We retain your personal information for as long as necessary to fulfill the purposes 
            outlined in this Privacy Policy, unless a longer retention period is required or permitted 
            by law.
          </p>
          <ul>
            <li><strong>Account Data:</strong> Retained while your account is active and for a reasonable period after account closure</li>
            <li><strong>Transaction Records:</strong> Retained for legal, tax, and accounting purposes (typically 7 years)</li>
            <li><strong>Usage Data:</strong> Retained for analytical purposes (typically 12-24 months)</li>
            <li><strong>Marketing Data:</strong> Retained until you withdraw consent or opt out</li>
            <li><strong>Legal Holds:</strong> Data may be retained longer if subject to legal proceedings or investigations</li>
          </ul>
          <p>
            After the retention period expires, we securely delete or anonymize your information.
          </p>
        </section>

        <section>
          <h2>7. Data Security</h2>
          <p>
            We implement industry-standard technical and organizational security measures to protect 
            your information, including:
          </p>
          <ul>
            <li>Encryption of data in transit (TLS/SSL) and at rest (AES-256)</li>
            <li>Secure authentication and access controls</li>
            <li>Regular security audits and vulnerability assessments</li>
            <li>Employee training on data protection and security practices</li>
            <li>Incident response and breach notification procedures</li>
            <li>Multi-factor authentication options</li>
            <li>Secure backup and disaster recovery systems</li>
          </ul>
          <p>
            While we strive to protect your information, no method of transmission or storage is 
            100% secure. You acknowledge that you provide information at your own risk.
          </p>
        </section>

        <section>
          <h2>8. Your Privacy Rights</h2>

          <h3>8.1 General Rights</h3>
          <p>Depending on your location, you may have the following rights:</p>
          <ul>
            <li><strong>Access:</strong> Request access to the personal information we hold about you</li>
            <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
            <li><strong>Deletion:</strong> Request deletion of your personal information (subject to legal obligations)</li>
            <li><strong>Portability:</strong> Request a copy of your data in a structured, machine-readable format</li>
            <li><strong>Object:</strong> Object to processing of your personal information for certain purposes</li>
            <li><strong>Restrict:</strong> Request restriction of processing in certain circumstances</li>
            <li><strong>Withdraw Consent:</strong> Withdraw consent for processing based on consent</li>
          </ul>

          <h3>8.2 GDPR Rights (EEA, UK, Switzerland)</h3>
          <p>If you are in the EEA, UK, or Switzerland, you have additional rights under GDPR:</p>
          <ul>
            <li>Right to lodge a complaint with your local data protection authority</li>
            <li>Right to object to automated decision-making and profiling</li>
            <li>Right to be informed about data breaches affecting your personal data</li>
          </ul>

          <h3>8.3 CCPA Rights (California)</h3>
          <p>If you are a California resident, you have rights under the California Consumer Privacy Act (CCPA):</p>
          <ul>
            <li>Right to know what personal information we collect, use, disclose, and sell</li>
            <li>Right to delete personal information (subject to exceptions)</li>
            <li>Right to opt out of the sale of personal information (we do not sell your information)</li>
            <li>Right to non-discrimination for exercising your privacy rights</li>
          </ul>

          <h3>8.4 How to Exercise Your Rights</h3>
          <p>
            To exercise any of these rights, please contact us at <strong>support@blackkiteai.com</strong>. 
            We will respond to your request within 30 days (or as required by applicable law). We may 
            require verification of your identity before processing your request.
          </p>

          <h3>8.5 Marketing Communications</h3>
          <p>
            You can opt out of marketing emails by clicking the "unsubscribe" link in any marketing 
            email or by contacting us directly. Note that you will still receive service-related 
            communications even if you opt out of marketing.
          </p>
        </section>

        <section>
          <h2>9. International Data Transfers</h2>
          <p>
            Your information may be transferred to and processed in countries other than your country 
            of residence, including the United States and other jurisdictions where our service providers 
            operate. These countries may have different data protection laws than your jurisdiction.
          </p>
          <p>
            When we transfer personal data from the EEA, UK, or Switzerland to other countries, we 
            implement appropriate safeguards, including:
          </p>
          <ul>
            <li>Standard Contractual Clauses approved by the European Commission</li>
            <li>Adequacy decisions recognizing equivalent data protection</li>
            <li>Binding Corporate Rules or other approved mechanisms</li>
          </ul>
        </section>

        <section>
          <h2>10. Children's Privacy</h2>
          <p>
            Our Service is not intended for individuals under the age of 18. We do not knowingly 
            collect personal information from children under 18. If you are a parent or guardian and 
            believe your child has provided us with personal information, please contact us immediately. 
            We will take steps to delete such information from our systems.
          </p>
        </section>

        <section>
          <h2>11. Third-Party Links and Services</h2>
          <p>
            Our Service may contain links to third-party websites, applications, or services that are 
            not owned or controlled by BlackKiteAI. This Privacy Policy does not apply to third-party 
            services. We are not responsible for the privacy practices of third parties. We encourage 
            you to review the privacy policies of any third-party services you access.
          </p>
        </section>

        <section>
          <h2>12. AI and Machine Learning</h2>
          <p>
            Our Service uses artificial intelligence and machine learning technologies to provide 
            risk scoring, threat intelligence, and security analytics. Your data may be used to:
          </p>
          <ul>
            <li>Train and improve our AI models</li>
            <li>Generate personalized risk assessments and recommendations</li>
            <li>Identify patterns and anomalies in cybersecurity data</li>
            <li>Enhance the accuracy and performance of our algorithms</li>
          </ul>
          <p>
            We implement measures to protect your privacy in our AI processes, including data 
            anonymization, access controls, and model security. However, AI-generated outputs may 
            contain errors or inaccuracies, and you should verify any critical information.
          </p>
        </section>

        <section>
          <h2>13. Data Breach Notification</h2>
          <p>
            In the event of a data breach that affects your personal information, we will notify you 
            and relevant authorities as required by applicable law. We will provide information about 
            the breach, the types of data affected, and steps we are taking to address the situation.
          </p>
        </section>

        <section>
          <h2>14. Changes to This Privacy Policy</h2>
          <p>
            We may update this Privacy Policy from time to time to reflect changes in our practices, 
            technology, legal requirements, or other factors. When we make material changes, we will:
          </p>
          <ul>
            <li>Update the "Last Updated" date at the top of this policy</li>
            <li>Notify you through the Service or via email</li>
            <li>Obtain your consent if required by law</li>
          </ul>
          <p>
            Your continued use of the Service after such changes constitutes acceptance of the updated 
            Privacy Policy. We encourage you to review this policy periodically.
          </p>
        </section>

        <section>
          <h2>15. Do Not Track Signals</h2>
          <p>
            Some browsers support "Do Not Track" (DNT) signals. Currently, there is no industry 
            consensus on how to respond to DNT signals. Our Service does not currently respond to 
            DNT browser signals or similar mechanisms.
          </p>
        </section>

        <section>
          <h2>16. Contact Us</h2>
          <p>
            If you have any questions, concerns, or requests regarding this Privacy Policy or our 
            data practices, please contact us:
          </p>
          <div className="contact-info">
            <p><strong>BlackKiteAI Privacy Team</strong></p>
            <p>Email: support@blackkiteai.com</p>
            <p>Subject Line: Privacy Inquiry</p>
            <p>Response Time: Within 30 days</p>
          </div>
          <p>
            For GDPR-related inquiries, you may also contact our Data Protection Officer (DPO) at 
            the same email address with "DPO Inquiry" in the subject line.
          </p>
        </section>

        <section className="consent">
          <h2>Consent</h2>
          <p>
            By using the Service, you acknowledge that you have read and understood this Privacy 
            Policy and consent to the collection, use, and disclosure of your information as described 
            herein.
          </p>
        </section>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
